import React from 'react';
import { Card } from '../../../shared/components/cards';
import type { BaseComponentProps, Job } from '../../../shared/types/common';

export interface JobCardProps extends BaseComponentProps {
  job: Job;
  onJobClick?: (job: Job) => void;
  showTags?: boolean;
  showImage?: boolean;
  compact?: boolean;
}

/**
 * JobCard component for displaying job information
 * Built for the jobs domain with consistent styling
 */
export const JobCard: React.FC<JobCardProps> = ({
  job,
  onJobClick,
  showTags = true,
  showImage = true,
  compact = false,
  className = '',
  ...props
}) => {
  const isUrgent = job.spotlight || job.priority;
  
  const handleClick = () => {
    if (onJobClick) {
      onJobClick(job);
    } else if (job.url) {
      window.open(job.url, '_blank');
    }
  };

  const cardClasses = [
    'job-card',
    job.spotlight && 'spotlight-job',
    onJobClick && 'cursor-pointer',
    className
  ].filter(Boolean).join(' ');

  return (
    <Card
      className={cardClasses}
      onClick={handleClick}
      hoverable={!!onJobClick}
      {...props}
    >
      <div className={`d-flex ${compact ? 'align-items-center' : ''}`}>
        {showImage && job.image && (
          <div className={`${compact ? 'me-3' : 'col-2 p-3'}`} style={{ minHeight: compact ? 'auto' : 40 }}>
            <img 
              src={job.image} 
              alt={`${job.practice_name} logo`}
              className="img-fluid rounded-circle"
              style={{ width: compact ? 40 : 'auto', height: compact ? 40 : 'auto' }}
            />
          </div>
        )}

        <div className={`${showImage && job.image ? (compact ? 'flex-grow-1' : 'col-10') : 'col-12'} d-flex flex-column justify-content-center`}>
          <div className="job-list-content">
            {isUrgent && (
              <span className="badge bg-danger mb-2">
                <i className="fa fa-clock me-1"></i> Urgent
              </span>
            )}
            
            <div className="d-flex flex-row align-items-center pe-2 mb-2">
              <h5 className={`mb-0 ${compact ? 'h6' : ''}`}>
                {job.title}
              </h5>
            </div>

            <div className="d-flex mb-2 flex-wrap gap-1">
              <span className="badge bg-light text-dark">
                <i className="fa fa-briefcase me-1"></i> {job.practice_name}
              </span>
              <span className="badge bg-light text-dark">
                <i className="fa fa-map-marker me-1"></i> {job.practice_city}, {job.practice_state}
              </span>
              {job.compensation && (
                <span className="badge bg-success">
                  <i className="fa fa-money me-1"></i> {job.compensation}
                </span>
              )}
            </div>

            {showTags && job.position_type && (
              <div className="d-flex flex-wrap gap-1">
                <span className="badge bg-secondary">
                  {job.position_type}
                </span>
              </div>
            )}

            {job.closed && (
              <div className="mt-2">
                <span className="badge bg-warning text-dark">Position Filled</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default JobCard;
